// src/pages/income/cafe/GeumnaraeIncomeList.jsx
import React, { useEffect, useMemo, useState } from "react";
import styles from "./GasanIncomeList.module.css"; // ✅ CSS 재사용
import api from "../../../api";
import * as XLSX from "xlsx";


const DEBUG = true;
const CATEGORY = "CAFE_INN 금나래점";
const SUBCATEGORY = "매출액";

export default function GeumnaraeIncomeList() {

  // ✅ 페이지 타이틀 & 탭 제목 설정
  useEffect(() => {
    document.title = "CAFE_INN 금나래점 수입목록";
  }, []); // eslint-disable-line

  const year = new Date().getFullYear();
  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate] = useState(`${year}-12-31`);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);


  // ✅ 목록 조회 (금나래점)
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = {
          category: CATEGORY,
          subcategory: SUBCATEGORY,
          search: search || undefined,
          ordering: "-sales_date,-seq",
        };
        const res = await api.get("/income/cafe/geumnarae/", { params }); // ✅ 금나래점용 API
        const data = res?.data;
        const list = Array.isArray(data) ? data : data?.results ?? [];
        if (!ignore) setRows(list);
        if (DEBUG) console.log("✅ [금나래점 수입목록]", list.length, "건 로드됨");
      } catch (e) {
        if (!ignore)
          setError(e?.response?.data?.detail || e?.message || "불러오기 실패");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [search]);

  // ✅ 기간 필터 + 매출일 내림차순 정렬
  const filtered = useMemo(() => {
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (to) to.setHours(23, 59, 59, 999);
    return rows
      .filter((r) => {
        const d = r?.sales_date ? new Date(r.sales_date) : null;
        if (!d) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      })
      .sort((a, b) => {
        const da = a.sales_date ?? "";
        const db = b.sales_date ?? "";
        if (db > da) return 1;
        if (db < da) return -1;
        return (b.seq ?? 0) - (a.seq ?? 0); // 같은 날짜면 seq 내림차순
      });
  }, [rows, fromDate, toDate]);

  // ✅ 합계 계산
  const totalAmount = useMemo(
    () => filtered.reduce((a, r) => a + (Number(r?.amount) || 0), 0),
    [filtered]
  );

  // excel 다운로드 
  const downloadExcel = () => {
    if (!filtered.length) return alert("다운로드할 데이터가 없습니다.");
  
    const toN = (v) => Number(String(v || "0").replace(/,/g, "")) || 0;
  
    const sums = filtered.reduce((acc, r) => {
      const p = typeof r.payments === "string" ? JSON.parse(r.payments) : r.payments;
      acc.총금액 += toN(r.amount);
      acc.카드결제 += toN(p?.card);
      acc.현금현장결제 += toN(p?.cash_onsite);
      acc.현금계좌이체 += toN(p?.cash_transfer);
      acc.현금영수증 += toN(p?.cash_receipt);
      acc.카카오페이 += toN(p?.kakaopay);
      acc.제로페이 += toN(p?.zeropay);
      acc.서울페이 += toN(p?.seoulpay);
      acc.토스페이 += toN(p?.tosspay);
      acc.티머니 += toN(p?.tmoney);
      acc.쿠팡이츠매출액 += toN(p?.coupang);
      acc.쿠팡이츠현금영수증 += toN(p?.coupang_receipt);
      acc.배달의민족매출액 += toN(p?.baemin);
      acc.배달의민족현금영수증 += toN(p?.baemin_receipt);
      return acc;
    }, {
      총금액:0, 카드결제:0, 현금현장결제:0, 현금계좌이체:0, 현금영수증:0,
      카카오페이:0, 제로페이:0, 서울페이:0, 토스페이:0, 티머니:0,
      쿠팡이츠매출액:0, 쿠팡이츠현금영수증:0, 배달의민족매출액:0, 배달의민족현금영수증:0,
    });
  
    const sumRow = { 매출일: "합계", 기안번호: "", 비고: "", ...sums };
  
    const dataRows = filtered.map((r) => {
      const p = typeof r.payments === "string" ? JSON.parse(r.payments) : r.payments;
      return {
        매출일: r.sales_date,
        기안번호: r.draft_no || String(r.seq ?? "").padStart(3, "0"),
        총금액: toN(r.amount),
        카드결제: toN(p?.card),
        현금현장결제: toN(p?.cash_onsite),
        현금계좌이체: toN(p?.cash_transfer),
        현금영수증: toN(p?.cash_receipt),
        카카오페이: toN(p?.kakaopay),
        제로페이: toN(p?.zeropay),
        서울페이: toN(p?.seoulpay),
        토스페이: toN(p?.tosspay),
        티머니: toN(p?.tmoney),
        쿠팡이츠매출액: toN(p?.coupang),
        쿠팡이츠현금영수증: toN(p?.coupang_receipt),
        배달의민족매출액: toN(p?.baemin),
        배달의민족현금영수증: toN(p?.baemin_receipt),
        비고: r.memo || "",
      };
    });
  
    const ws = XLSX.utils.json_to_sheet([sumRow, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "금나래점수입목록");
    XLSX.writeFile(wb, `금나래점_수입목록_${fromDate}_${toDate}.xlsx`);
  };

  // ✅ 수정 시작
  const startEdit = (r) => {
    const payments = typeof r.payments === "string" ? JSON.parse(r.payments) : r.payments;
    setEditId(r.id);
    setEditForm({
      sales_date: r.sales_date || "",
      draft_no: r.draft_no || "",
      memo: r.memo || "",
      payments: { ...payments },
    });
  };

  // ✅ 수정 취소
  const cancelEdit = () => {
    setEditId(null);
    setEditForm({});
  };

  // ✅ 수정 저장
  const saveEdit = async (r) => {
    if (editSaving) return;
    setEditSaving(true);
    try {
      const payload = {
        sales_date: editForm.sales_date,
        memo: editForm.memo,
        payments: editForm.payments,
        amount: Object.entries(editForm.payments)
          .filter(([k]) => !["cash_receipt"].includes(k))
          .reduce((s, [, v]) => s + (Number(String(v || "0").replace(/,/g, "")) || 0), 0),
      };
      await api.patch(`/income/cafe/geumnarae/${r.id}/`, payload);
      const savedForm = { ...editForm };
      const savedPayments = { ...editForm.payments };
      alert("✅ 수정 완료!");
      setEditId(null);
      setEditForm({});
      setRows((prev) =>
        prev.map((row) =>
          row.id === r.id
            ? {
                ...row,
                sales_date: savedForm.sales_date,
                memo: savedForm.memo,
                payments: savedPayments,
                amount: payload.amount,
              }
            : row
        )
      );
    } catch (err) {
      alert("수정 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setEditSaving(false);
    }
  };

  // ✅ 삭제
  const deleteRow = async (r) => {
    const confirmed = window.confirm("정말 삭제하시겠습니까?");
    if (!confirmed) return;
    try {
      await api.delete(`/income/cafe/geumnarae/${r.id}/`);
      alert("삭제되었습니다.");
      setRows((prev) => prev.filter((row) => row.id !== r.id));
    } catch (err) {
      alert("삭제 실패: " + (err?.response?.data?.detail || err.message));
    }
  };

  // ✅ 인라인 금액 입력 핸들러
  // ✅ 결제 취소·환불 등 차감 건 기록용 — 맨 앞의 "-"는 부호로 보존(GeumnaraeIncomeCreate.jsx의
  // handlePayChange와 동일 원칙)
  const handleEditPay = (key, val) => {
    const isNegative = val.trim().startsWith("-");
    const digits = val.replace(/[^\d]/g, "");
    setEditForm((prev) => ({
      ...prev,
      payments: { ...prev.payments, [key]: (isNegative ? "-" : "") + digits },
    }));
  };

  if (loading) return <div style={{ padding: 16 }}>불러오는 중…</div>;
  if (error) return <div style={{ padding: 16, color: "red" }}>{error}</div>;

  // ✅ 렌더링
  return (
    <div style={{ padding: 16 }}>
      <h2>CAFE_INN 금나래점 수입목록</h2>

      {/* 🔍 필터 바 */}
      <div className={styles.filterBar}>
        <div className={styles.filterItem}>
          <div className={styles.label}>기간</div>
          <div className={styles.dateRow}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={styles.inputBase}
            />
            <span className={styles.dateDash}>~</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={styles.inputBase}
            />
          </div>
        </div>

        <div className={styles.filterItem}>
          <div className={styles.label}>검색</div>
          <div className={styles.searchRow}>
            <input
              className={`${styles.inputBase} ${styles.inputSearch}`}
              placeholder="기안번호/제목/비고 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSearch(searchInput.trim());
              }}
            />
            <button
              type="button"
              className={styles.btn}
              onClick={() => setSearch(searchInput.trim())}
            >
              검색
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={() => {
                setFromDate(`${year}-01-01`);
                setToDate(`${year}-12-31`);
                setSearch("");
                setSearchInput("");
              }}
            >
              초기화
            </button>
            <button
              type="button"
              onClick={downloadExcel}
              className={styles.btnPrimary}
            >
              엑셀 다운로드
            </button>
          </div>
        </div>
      </div>

      {/* 📊 테이블 */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th rowSpan="2">매출일</th>
            <th rowSpan="2">기안번호</th>
            <th rowSpan="2">총금액</th>
            <th rowSpan="2">카드결제</th>
            <th colSpan="3">현금결제</th>
            <th colSpan="4">페이</th>
            <th rowSpan="2">티머니</th>
            <th colSpan="2">쿠팡이츠</th>
            <th colSpan="2">배달의민족</th>
            <th rowSpan="2">비고</th>
          </tr>
          <tr>
            <th>현장결제</th>
            <th>계좌이체</th>
            <th>현금영수증</th>
            <th>카카오</th>
            <th>제로</th>
            <th>서울</th>
            <th>토스</th>

            <th>매출액</th>
            <th>현금영수증</th>
            <th>매출액</th>
            <th>현금영수증</th>
          </tr>
        </thead>

        <tbody>
          {/* ✅ 합계 행 */}
          {(() => {
            const sums = {
              amount: 0,
              card: 0,
              cash_onsite: 0,
              cash_transfer: 0,
              cash_receipt: 0,
              kakaopay: 0,
              zeropay: 0,
              seoulpay: 0,
              tosspay: 0,
              tmoney: 0,
              
              coupang: 0,
              coupang_receipt: 0,
              baemin: 0,
              baemin_receipt: 0,
            };

            filtered.forEach((r) => {
              const payments =
                typeof r.payments === "string" ? JSON.parse(r.payments) : r.payments;
              const toNum = (v) => {
                const num = Number(String(v || "0").replace(/,/g, ""));
                return isNaN(num) ? 0 : num;
              };
              sums.amount += toNum(r.amount);
              sums.card += toNum(payments?.card);
              sums.cash_onsite += toNum(payments?.cash_onsite);
              sums.cash_transfer += toNum(payments?.cash_transfer);
              sums.cash_receipt += toNum(payments?.cash_receipt);
              sums.kakaopay += toNum(payments?.kakaopay);
              sums.zeropay += toNum(payments?.zeropay);
              sums.seoulpay += toNum(payments?.seoulpay);
              sums.tosspay += toNum(payments?.tosspay);
              sums.tmoney += toNum(payments?.tmoney);
              
              sums.coupang += toNum(payments?.coupang);
              sums.coupang_receipt += toNum(payments?.coupang_receipt);
              sums.baemin += toNum(payments?.baemin);
              sums.baemin_receipt += toNum(payments?.baemin_receipt);

            });

            return (
              <tr style={{ borderBottom: "2px solid #e5e7eb", background: "#fafafa" }}>
                <td colSpan="2" style={{ textAlign: "center", fontWeight: "bold" }}>
                  합계
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.amount.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.card.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.cash_onsite.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.cash_transfer.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.cash_receipt.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.kakaopay.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.zeropay.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.seoulpay.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.tosspay.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.tmoney.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.coupang.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.coupang_receipt.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.baemin.toLocaleString()}
                </td>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {sums.baemin_receipt.toLocaleString()}
                </td>
              </tr>
            );
          })()}

          {/* ✅ 데이터 목록 */}
          {filtered.map((r) => {
            const payments =
              typeof r.payments === "string" ? JSON.parse(r.payments) : r.payments;

            const toNumber = (val) => {
              if (val === null || val === undefined || val === "") return "";
              const num = Number(String(val).replace(/,/g, ""));
              return isNaN(num) ? "" : num.toLocaleString();
            };

            const isEditing = editId === r.id;

            const editCell = (key) => (
              <td style={{ textAlign: "right" }}>
                <input
                  type="text"
                  value={editForm.payments?.[key] ?? ""}
                  onChange={(e) => handleEditPay(key, e.target.value)}
                  style={{
                    width: "80px", textAlign: "right",
                    border: "1px solid #60a5fa", borderRadius: "4px",
                    padding: "2px 4px", fontSize: "12px",
                  }}
                />
              </td>
            );

            return (
              <tr key={r.id} style={isEditing ? { background: "#eff6ff" } : {}}>
                <td>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editForm.sales_date}
                      onChange={(e) => setEditForm((p) => ({ ...p, sales_date: e.target.value }))}
                      style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px" }}
                    />
                  ) : r.sales_date}
                </td>
                <td>{r.draft_no || String(r.seq ?? "").padStart(3, "0")}</td>
                <td style={{ textAlign: "right" }}>
                  {isEditing
                    ? Object.entries(editForm.payments || {})
                        .filter(([k]) => !["cash_receipt"].includes(k))
                        .reduce((s, [, v]) => s + (Number(String(v || "0").replace(/,/g, "")) || 0), 0)
                        .toLocaleString()
                    : toNumber(r.amount)
                  }
                </td>
                {isEditing ? editCell("card")            : <td style={{ textAlign: "right" }}>{toNumber(payments?.card)}</td>}
                {isEditing ? editCell("cash_onsite")     : <td style={{ textAlign: "right" }}>{toNumber(payments?.cash_onsite)}</td>}
                {isEditing ? editCell("cash_transfer")   : <td style={{ textAlign: "right" }}>{toNumber(payments?.cash_transfer)}</td>}
                {isEditing ? editCell("cash_receipt")    : <td style={{ textAlign: "right" }}>{toNumber(payments?.cash_receipt)}</td>}
                {isEditing ? editCell("kakaopay")        : <td style={{ textAlign: "right" }}>{toNumber(payments?.kakaopay)}</td>}
                {isEditing ? editCell("zeropay")         : <td style={{ textAlign: "right" }}>{toNumber(payments?.zeropay)}</td>}
                {isEditing ? editCell("seoulpay")        : <td style={{ textAlign: "right" }}>{toNumber(payments?.seoulpay)}</td>}
                {isEditing ? editCell("tosspay")         : <td style={{ textAlign: "right" }}>{toNumber(payments?.tosspay)}</td>}
                {isEditing ? editCell("tmoney")          : <td style={{ textAlign: "right" }}>{toNumber(payments?.tmoney)}</td>}
                {isEditing ? editCell("coupang")         : <td style={{ textAlign: "right" }}>{toNumber(payments?.coupang)}</td>}
                {isEditing ? editCell("coupang_receipt") : <td style={{ textAlign: "right" }}>{toNumber(payments?.coupang_receipt)}</td>}
                {isEditing ? editCell("baemin")          : <td style={{ textAlign: "right" }}>{toNumber(payments?.baemin)}</td>}
                {isEditing ? editCell("baemin_receipt")  : <td style={{ textAlign: "right" }}>{toNumber(payments?.baemin_receipt)}</td>}
                <td>
                  {isEditing ? (
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        className={styles.btnLink}
                        onClick={() => saveEdit(r)}
                        disabled={editSaving}
                        style={{ color: "#1a73e8" }}
                      >
                        {editSaving ? "저장중" : "저장"}
                      </button>
                      <button
                        className={styles.btnLink}
                        onClick={cancelEdit}
                        style={{ color: "#6b7280" }}
                      >
                        취소
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        className={styles.btnLink}
                        onClick={() => startEdit(r)}
                      >
                        수정
                      </button>
                      <button
                        className={styles.btnLink}
                        onClick={() => deleteRow(r)}
                        style={{ color: "#dc2626" }}
                      >
                        삭제
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


